import { popStackPageByName, pushStackPage } from '@ardoq/stack-page-manager';
import { DrawerScroll, TRANSITION_TIME } from './atoms';
import { DrawerProvider } from './DrawerProvider';
import { DrawerBodyProps, DrawerProps, ShowDrawerProps } from './types';
import { DrawerHeader } from './DrawerHeader';
import { DrawerWrapper } from './DrawerWrapper';
import { Box, SpaceVariant } from '@ardoq/layout';
import { forwardRef, ReactNode } from 'react';

export const showDrawer = ({
  stackPageName,
  shouldCloseOnEscapeKey = false,
  shouldCloseOnBackdropClick = false,
  renderDrawer,
  onClose = () => {},
  hasBackdrop,
  id,
  ariaLabel,
}: ShowDrawerProps) => {
  const closeDrawer = () => {
    setTimeout(() => {
      popStackPageByName(stackPageName);
    }, TRANSITION_TIME);
    onClose();
  };

  pushStackPage(
    ({ stackPageIsInFocus }) => (
      <DrawerProvider
        stackPageIsInFocus={stackPageIsInFocus}
        shouldCloseOnEscapeKey={shouldCloseOnEscapeKey}
        shouldCloseOnBackdropClick={shouldCloseOnBackdropClick}
        closeDrawer={closeDrawer}
        renderDrawer={renderDrawer}
        hasBackdrop={hasBackdrop}
        id={id}
        ariaLabel={ariaLabel}
      />
    ),
    {
      stackPageName,
      isNonModal: hasBackdrop === false,
    }
  );

  return {
    closeDrawer,
  };
};

const DrawerMainContent = ({
  children,
  hasHeader,
  paddingX,
  paddingTop,
  paddingBottom,
}: {
  children: ReactNode;
  hasHeader?: boolean;
  paddingX?: SpaceVariant;
  paddingTop?: SpaceVariant;
  paddingBottom?: SpaceVariant;
}) => (
  <Box
    paddingX={paddingX ?? 'large'}
    paddingBottom={paddingBottom ?? 'large'}
    paddingTop={paddingTop ?? (hasHeader ? 'none' : 'large')}
    height="full"
  >
    {children}
  </Box>
);

export const DrawerBody = ({
  children,
  hideScroll,
  hasHeader,
  paddingX,
  paddingTop,
  paddingBottom,
}: DrawerBodyProps & { hasHeader?: boolean }) => {
  return hideScroll ? (
    <DrawerMainContent
      hasHeader={hasHeader}
      paddingX={paddingX}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
    >
      {children}
    </DrawerMainContent>
  ) : (
    <DrawerScroll>
      <DrawerMainContent
        hasHeader={hasHeader}
        paddingX={paddingX}
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
      >
        {children}
      </DrawerMainContent>
    </DrawerScroll>
  );
};

export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(
  (
    {
      drawerSize,
      orientation,
      title,
      icon,
      subtitle,
      controls,
      children,
      footer,
      hideScroll,
      paddingX,
      paddingTop,
      paddingBottom,
      dataTourId,
      hasSquareCorner,
      width,
    },
    ref
  ) => {
    return (
      <DrawerWrapper
        drawerSize={drawerSize}
        orientation={orientation}
        dataTourId={dataTourId}
        hasSquareCorner={hasSquareCorner}
        width={width}
        ref={ref}
      >
        {Boolean(title) && (
          <DrawerHeader
            title={title}
            icon={icon}
            subtitle={subtitle}
            controls={controls}
          />
        )}
        <DrawerBody
          hideScroll={hideScroll}
          hasHeader={Boolean(title)}
          paddingX={paddingX}
          paddingTop={paddingTop}
          paddingBottom={paddingBottom}
        >
          {children}
        </DrawerBody>
        {footer}
      </DrawerWrapper>
    );
  }
);
